import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import MarginHistoryModal from "./marginHistoryPanel";
import { addAdminMargin, updateAdminMargin } from "../../Redux/Actions/propertiesResaleActions";

const AdminMarginPopup = ({ isOpen, onClose, propertyId, existingData = null }) => {
  const dispatch = useDispatch();
  const isEdit = !!existingData;

  const [form, setForm] = useState({
    admin_margin_type: "percentage",
    admin_margin_value: "",
  });
  const [loading, setLoading] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false); 

  useEffect(() => {
    if (existingData) {
      setForm({
        admin_margin_type: existingData.admin_margin_type || "percentage",
        admin_margin_value: existingData.admin_margin_value || "",
      });
    } else {
      setForm({ admin_margin_type: "percentage", admin_margin_value: "" });
    }
    setShowHistoryModal(false);
  }, [existingData, isOpen]);

  if (!isOpen) return null;

  const marginTypes = [
    { value: "percentage", label: "Percentage", icon: "%" },
    { value: "flat", label: "Flat Amount", icon: "₹" },
    { value: "per_sq_ft", label: "Per Sq.ft", icon: "□" },
  ];

  const handleSubmit = async () => {
    if (!form.admin_margin_value) return;
    setLoading(true);
    const payload = {
      property_id: propertyId,
      admin_margin_type: form.admin_margin_type,
      admin_margin_value: String(form.admin_margin_value),
    };
    const action = isEdit ? updateAdminMargin : addAdminMargin;
    const res = await dispatch(action(payload));
    setLoading(false);
    if (res?.statusCode === 200) onClose();
  };

  return (
    <>
      <div style={styles.overlay} onClick={onClose}>
        <div style={styles.modal} onClick={(e) => e.stopPropagation()}>

          {/* Header */}
          <div style={styles.header}>
            <div style={styles.headerLeft}>
              <div style={styles.iconBox}>💰</div>
              <div>
                <h2 style={styles.title}>{isEdit ? "Update" : "Add"} Admin Margin</h2>
                <p style={styles.subtitle}>Configure the admin margin for this property</p>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <button
                style={{
                  ...styles.closeBtn,
                  background: "rgba(44,71,145,0.08)",
                  border: "1px solid rgba(44,71,145,0.2)",
                  color: "#2c4791",
                  fontSize: "15px",
                  fontWeight: 700,
                }}
                onClick={() => setShowHistoryModal(true)}
                title="View Margin History"
              >
                ℹ
              </button>
              <button style={styles.closeBtn} onClick={onClose}>✖</button>
            </div>
          </div>

          <div style={styles.section}>
            <label style={styles.label}>Margin Type</label>
            <div style={styles.typeGrid}>
              {marginTypes.map((type) => (
                <button
                  key={type.value}
                  style={{
                    ...styles.typeBtn,
                    ...(form.admin_margin_type === type.value ? styles.typeBtnActive : {}),
                  }}
                  onClick={() => setForm((f) => ({ ...f, admin_margin_type: type.value }))}
                >
                  <span style={styles.typeIcon}>{type.icon}</span>
                  <span style={styles.typeLabel}>{type.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div style={styles.section}>
            <label style={styles.label}>
              Margin Value{" "}
              {form.admin_margin_type === "percentage"
                ? "(in %)"
                : form.admin_margin_type === "flat"
                  ? "(in ₹)"
                  : "(per sq.ft in ₹)"}
            </label>
            <div style={styles.inputWrapper}>
              <span style={styles.inputPrefix}>
                {form.admin_margin_type === "percentage" ? "%" : "₹"}
              </span>
              <input
                type="number"
                placeholder="Enter value"
                value={form.admin_margin_value}
                onChange={(e) => setForm((f) => ({ ...f, admin_margin_value: e.target.value }))}
                style={styles.input}
              />
            </div>
          </div>

          {form.admin_margin_value && (
            <div style={styles.preview}>
              <span style={styles.previewLabel}>Preview</span>
              <span style={styles.previewValue}>
                {form.admin_margin_type === "percentage" && `${form.admin_margin_value}% of total price`}
                {form.admin_margin_type === "flat" && `₹${Number(form.admin_margin_value).toLocaleString("en-IN")} flat`}
                {form.admin_margin_type === "per_sq_ft" && `₹${form.admin_margin_value} per sq.ft`}
              </span>
            </div>
          )}

          <div style={styles.actions}>
            <button style={styles.cancelBtn} onClick={onClose}>Cancel</button>
            <button
              style={{ ...styles.submitBtn, opacity: loading ? 0.7 : 1 }}
              onClick={handleSubmit}
              disabled={loading || !form.admin_margin_value}
            >
              {loading ? "Saving..." : isEdit ? "Update Margin" : "Add Margin"}
            </button>
          </div>
        </div>
      </div>

      <MarginHistoryModal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        propertyId={propertyId}
        filterActions={["add_admin_margin", "update_admin_margin"]}
      />
    </>
  );
};

const styles = {
  overlay: {
    position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
    background: "rgba(0,0,0,0.45)", backdropFilter: "blur(6px)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 10000, animation: "fadeIn 0.2s ease",
  },
  modal: {
    background: "#ffffff",
    borderRadius: "24px", padding: "32px",
    width: "100%", maxWidth: "520px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 24px 60px rgba(0,0,0,0.12)",
  },
  header: {
    display: "flex", justifyContent: "space-between",
    alignItems: "flex-start", marginBottom: "28px",
  },
  headerLeft: { display: "flex", alignItems: "center", gap: "16px" },
  iconBox: {
    fontSize: "28px", background: "rgba(16,185,129,0.1)",
    border: "1px solid rgba(16,185,129,0.25)",
    borderRadius: "14px", padding: "10px",
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  title: { color: "#0f172a", fontSize: "20px", fontWeight: 700, margin: 0 },
  subtitle: { color: "#64748b", fontSize: "13px", margin: "4px 0 0 0" },
  closeBtn: {
    background: "#f1f5f9", border: "1px solid #e2e8f0",
    color: "#64748b", borderRadius: "50%", width: "36px", height: "36px",
    display: "flex", alignItems: "center", justifyContent: "center",
    cursor: "pointer",
  },
  section: { marginBottom: "24px" },
  label: {
    display: "block", color: "#64748b", fontSize: "13px",
    fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "10px",
  },
  typeGrid: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "12px" },
  typeBtn: {
    display: "flex", flexDirection: "column", alignItems: "center",
    gap: "8px", padding: "16px 12px",
    background: "#f8fafc", border: "1px solid #e2e8f0",
    borderRadius: "14px", cursor: "pointer", transition: "all 0.2s",
  },
  typeBtnActive: {
    background: "rgba(44,71,145,0.08)", border: "1px solid rgba(44,71,145,0.4)",
    boxShadow: "0 0 12px rgba(44,71,145,0.1)",
  },
  typeIcon: { fontSize: "22px", color: "#2c4791", fontWeight: 700 },
  typeLabel: { color: "#0f172a", fontSize: "13px", fontWeight: 600 },
  inputWrapper: {
    display: "flex", alignItems: "center",
    background: "#f8fafc", border: "1px solid #e2e8f0",
    borderRadius: "12px", overflow: "hidden",
  },
  inputPrefix: {
    padding: "14px 16px", background: "rgba(44,71,145,0.08)",
    color: "#2c4791", fontWeight: 700, fontSize: "16px",
    borderRight: "1px solid #e2e8f0",
  },
  input: {
    flex: 1, padding: "14px 16px", background: "transparent",
    border: "none", outline: "none", color: "#0f172a",
    fontSize: "16px", fontFamily: "inherit",
  },
  preview: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    background: "rgba(16,185,129,0.06)", border: "1px solid rgba(16,185,129,0.25)",
    borderRadius: "12px", padding: "14px 18px", marginBottom: "24px",
  },
  previewLabel: { color: "#10b981", fontSize: "13px", fontWeight: 600 },
  previewValue: { color: "#0f172a", fontSize: "15px", fontWeight: 700 },
  actions: { display: "flex", gap: "12px" },
  cancelBtn: {
    flex: 1, padding: "14px", background: "#f8fafc",
    border: "1px solid #e2e8f0", borderRadius: "12px",
    color: "#64748b", fontSize: "15px", fontWeight: 600, cursor: "pointer",
  },
  submitBtn: {
    flex: 2, padding: "14px",
    background: "linear-gradient(135deg, #10b981, #059669)",
    border: "none", borderRadius: "12px", color: "white",
    fontSize: "15px", fontWeight: 700, cursor: "pointer",
    boxShadow: "0 4px 16px rgba(16,185,129,0.25)",
  },
};

export default AdminMarginPopup;